import React, { useCallback, useMemo } from 'react';
import type { MultiselectCustomerPartialEntity } from '../../../components/global/ConfigurationMultiselect/configurationMultiselectTypes';
import { ConfigurationRuleScopeEntityType, ConfigurationRuleScopeMode } from '@ydistri/api-sdk';
import { useScope } from './useScope';
import { Column, Row, Text } from '@ydistri/ds';
import MultiselectTag from './MultiselectTag';
import { useOpenScopeModal } from './useOpenScopeModal';
import { getScopeErrorText } from './scopeLib';
import { Popover } from 'antd';
import { MAIN_SCOPE_PRIORITY } from './scopeUtilsLib';
import { TemplateOrCalculationWithValue } from '../../../apis/api';

interface ScopeViewModeProps {
  scopeId: number;
  entityType: ConfigurationRuleScopeEntityType;
  loading?: boolean;
  options: MultiselectCustomerPartialEntity[];
  disabled?: boolean;
  tagWarnings?: Record<number, React.ReactNode | undefined>;
  /** The selected ids the server's report says are out of the general scope. */
  missingIds: number[];
  templateOrCalculation: TemplateOrCalculationWithValue;
}

const visibleLimit = 5;

const ScopeViewMode: React.FC<ScopeViewModeProps> = ({
  scopeId,
  entityType,
  options,
  disabled,
  tagWarnings,
  missingIds,
  templateOrCalculation,
}) => {
  const scope = useScope(scopeId, templateOrCalculation);
  const openModalHandler = useOpenScopeModal(scope);

  const selectionOption = scope?.entities[entityType].selectionOption;

  const selectedOptions = useMemo(() => {
    const selectedIds = scope?.entities[entityType].selectedIds ?? [];
    return selectedIds.map(o => ({ id: o, error: missingIds.includes(o) }));
  }, [entityType, missingIds, scope?.entities]);

  const tagRenderer = useCallback(
    (option: { id: number; error: boolean }) => {
      const error = getScopeErrorText(
        scope?.priority ?? MAIN_SCOPE_PRIORITY,
        option.error,
        entityType,
      );

      return (
        <MultiselectTag
          key={option.id}
          entityType={entityType}
          closable={false}
          entityId={option.id}
          error={error}
          warning={error ? undefined : tagWarnings?.[option.id]}
          onClick={openModalHandler}
          label={options.find(item => item.id === option.id)?.label}
          disabled={disabled}
        />
      );
    },
    [disabled, entityType, openModalHandler, options, scope?.priority, tagWarnings],
  );

  const popoverContent = useMemo(() => {
    return (
      <Row $gap="0.25rem" $flexWrap="wrap" $maxWidth="25rem">
        {selectedOptions.map(tagRenderer)}
      </Row>
    );
  }, [selectedOptions, tagRenderer]);

  if (selectionOption === ConfigurationRuleScopeMode.All) {
    return '';
  } else {
    return (
      <Column $gap="0.5rem">
        <Row $gap="0.25rem" $flexGrow={1} $flexWrap="wrap" $alignItems="center">
          {selectedOptions.length === 0 && 'None'}
          {selectedOptions.slice(0, visibleLimit).map(tagRenderer)}
          {selectedOptions.length > visibleLimit && (
            <Popover content={popoverContent}>
              <Text $semiBold>+{selectedOptions.length - visibleLimit} more</Text>
            </Popover>
          )}
        </Row>
      </Column>
    );
  }
};

export default ScopeViewMode;
